import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  authApi,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  refreshAccessToken,
  setTokens,
  type ApiUser,
} from '../lib/api'

type AuthContextValue = {
  user: ApiUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshMe = useCallback(async () => {
    if (!getAccessToken() && !getRefreshToken()) {
      setUser(null)
      return
    }
    // If access token is missing/expired, refresh first then load profile.
    if (!getAccessToken() && getRefreshToken()) {
      const ok = await refreshAccessToken()
      if (!ok) {
        // Keep refresh token on transient network failures; only clear if refresh endpoint rejected it.
        if (!getRefreshToken()) {
          clearTokens()
          setUser(null)
        }
        return
      }
    }
    const { user: me } = await authApi.me()
    setUser(me)
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        await refreshMe()
      } catch {
        const ok = await refreshAccessToken()
        if (ok) {
          try {
            await refreshMe()
            return
          } catch {
            /* keep session tokens; user can retry on next navigation */
          }
        } else if (!getRefreshToken()) {
          clearTokens()
          setUser(null)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [refreshMe])

  // Keep session alive: refresh when tab becomes visible + periodically.
  useEffect(() => {
    const keepAlive = () => {
      if (!getRefreshToken()) return
      void refreshAccessToken().then((ok) => {
        if (!ok) return
        void authApi.me().then(({ user: me }) => setUser(me)).catch(() => {
          /* ignore */
        })
      })
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') keepAlive()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', keepAlive)
    const interval = window.setInterval(keepAlive, 12 * 60 * 60 * 1000) // every 12h
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', keepAlive)
      window.clearInterval(interval)
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    setTokens(res.token, res.refresh_token)
    await refreshMe()
  }, [refreshMe])

  const logout = useCallback(() => {
    clearTokens()
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshMe }),
    [user, loading, login, logout, refreshMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}

export function hasAnyRole(user: ApiUser | null, roles: string[]) {
  if (!user) return false
  return roles.some((role) => user.roles.includes(role))
}
